import { Outlet, Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import StoreHeader from "./StoreHeader";
import BottomNav from "./BottomNav";
import WhatsAppFab from "@/components/store/home/WhatsAppFab";
import DynamicTheme from "@/components/store/DynamicTheme";
import CookieConsent from "@/components/store/CookieConsent";
import { useStoreSettings } from "@/hooks/useStoreSettings";
import { useLegalData } from "@/hooks/useLegalData";
import { useStoreUrl } from "@/hooks/useStoreUrl";
import { useSEO } from "@/hooks/useSEO";

const pageVariants = {
  initial: { opacity: 0, y: 8 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.25, ease: "easeOut" } },
  exit: { opacity: 0, y: -6, transition: { duration: 0.15, ease: "easeIn" } },
};

export default function StoreLayout() {
  const { settings } = useStoreSettings();
  const legal = useLegalData();
  const { storeUrl } = useStoreUrl();
  const location = useLocation();

  useSEO();

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <DynamicTheme />
      <StoreHeader />
      <main className="flex-1 pb-16 md:pb-0">
        <AnimatePresence mode="wait">
          <motion.div
            key={location.pathname}
            variants={pageVariants}
            initial="initial"
            animate="animate"
            exit="exit"
          >
            <Outlet />
          </motion.div>
        </AnimatePresence>
      </main>

      {/* Desktop footer */}
      <footer className="hidden md:block border-t border-border bg-card mt-8 sm:mt-12">
        <div className="container mx-auto px-4 py-6 sm:py-10">
          <div className="grid gap-5 sm:gap-8 grid-cols-2 md:grid-cols-4 text-sm">
            <div className="col-span-2 sm:col-span-1">
              <h3 className="font-bold text-foreground mb-1.5 sm:mb-2 text-sm">
                {settings.store_name}
              </h3>
              <p className="text-muted-foreground text-xs leading-relaxed">
                Tudo o que você precisa em um só lugar. Qualidade, confiança e os
                melhores preços.
              </p>
              {legal.cnpj && (
                <p className="text-[10px] text-muted-foreground/70 mt-2">
                  CNPJ: {legal.cnpj}
                </p>
              )}
            </div>
            <div>
              <h4 className="font-semibold text-foreground mb-1.5 sm:mb-2 text-[10px] sm:text-xs uppercase tracking-wider">
                Institucional
              </h4>
              <ul className="space-y-1 sm:space-y-1.5 text-xs text-muted-foreground">
                <li>
                  <Link to={storeUrl("/catalog")} className="hover:text-primary transition-colors">
                    Nossos Produtos
                  </Link>
                </li>
                <li>
                  <Link to={storeUrl("/about")} className="hover:text-primary transition-colors">
                    Sobre Nós
                  </Link>
                </li>
                <li>
                  <Link to={storeUrl("/privacy")} className="hover:text-primary transition-colors">
                    Privacidade
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-foreground mb-1.5 sm:mb-2 text-[10px] sm:text-xs uppercase tracking-wider">
                Ajuda
              </h4>
              <ul className="space-y-1 sm:space-y-1.5 text-xs text-muted-foreground">
                <li>
                  <Link to={storeUrl("/exchanges")} className="hover:text-primary transition-colors">
                    Trocas
                  </Link>
                </li>
                <li>
                  <Link to={storeUrl("/payment-info")} className="hover:text-primary transition-colors">
                    Pagamento
                  </Link>
                </li>
                <li>
                  <Link to={storeUrl("/contact")} className="hover:text-primary transition-colors">
                    Contato
                  </Link>
                </li>
              </ul>
            </div>
            {/* Contact column */}
            {(legal.telefone || legal.email) && (
              <div>
                <h4 className="font-semibold text-foreground mb-1.5 sm:mb-2 text-[10px] sm:text-xs uppercase tracking-wider">
                  Contato
                </h4>
                <ul className="space-y-1 sm:space-y-1.5 text-xs text-muted-foreground">
                  {legal.telefone && <li>{legal.telefone}</li>}
                  {legal.email && <li className="truncate">{legal.email}</li>}
                  {legal.cidade && legal.estado && (
                    <li>{legal.cidade} - {legal.estado}</li>
                  )}
                </ul>
              </div>
            )}
          </div>
          <div className="mt-5 sm:mt-8 pt-3 sm:pt-4 border-t border-border/60 flex flex-col sm:flex-row items-center justify-between gap-1.5">
            <p className="text-[10px] sm:text-xs text-muted-foreground">
              © {new Date().getFullYear()} {settings.store_name}
              {legal.razaoSocial && (
                <span className="text-muted-foreground/60"> — {legal.razaoSocial}</span>
              )}
            </p>
            <p className="text-[10px] text-muted-foreground/50">
              Powered by <span className="font-semibold">Codxis</span>
            </p>
          </div>
        </div>
      </footer>

      {/* Mobile bottom nav */}
      <BottomNav />
      <WhatsAppFab />
      <CookieConsent />
    </div>
  );
}
